import type { SelectHTMLAttributes } from "react";
import { Icon } from "../icon/Icon";

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  label?: string;
  placeholder?: string;
  options?: Array<string | SelectOption>;
  error?: boolean;
  disabled?: boolean;
  size?: "large" | "medium";
}

export function Select({
  label,
  value,
  placeholder = "선택",
  options = [],
  disabled = false,
  error = false,
  size = "large",
  onChange,
  style,
  ...rest
}: SelectProps) {
  const height = size === "medium" ? 40 : 48;
  const font = size === "medium" ? "var(--font-size-body-2)" : "var(--font-size-body-1)";
  const hasValue = value != null && value !== "";
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6, fontFamily: "var(--font-sans)", ...style }}>
      {label && (
        <span
          style={{
            fontSize: "var(--font-size-label-1)",
            lineHeight: "var(--line-height-label-1)",
            fontWeight: 600,
            color: "var(--label-normal)",
          }}
        >
          {label}
        </span>
      )}
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          height,
          padding: "0 14px",
          borderRadius: "var(--radius-md)",
          background: disabled ? "var(--background-normal-alternative)" : "var(--background-normal-normal)",
          boxShadow: `inset 0 0 0 1px ${error ? "var(--status-negative)" : "var(--line-normal-normal)"}`,
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <select
          value={value ?? ""}
          disabled={disabled}
          onChange={onChange}
          style={{
            appearance: "none",
            WebkitAppearance: "none",
            flex: 1,
            border: "none",
            outline: "none",
            background: "transparent",
            fontFamily: "var(--font-sans)",
            fontSize: font,
            color: hasValue ? "var(--label-normal)" : "var(--label-assistive)",
            cursor: disabled ? "not-allowed" : "pointer",
          }}
          {...rest}
        >
          <option value="" disabled hidden>
            {placeholder}
          </option>
          {options.map((o) => {
            const val = typeof o === "string" ? o : o.value;
            const lab = typeof o === "string" ? o : o.label;
            return (
              <option key={val} value={val}>
                {lab}
              </option>
            );
          })}
        </select>
        <Icon
          name="chevronDown"
          size={20}
          style={{ color: "var(--label-alternative)", flex: "none", pointerEvents: "none" }}
        />
      </div>
    </label>
  );
}

export default Select;
